import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import {flagEnabled} from '../lib/serviceAvailability';

/**
 * FeatureFlagProvider — fetches the resolved flag map from
 * `GET /api/feature-flags` on boot, every 30 seconds and on focus. Retains the
 * last values in memory, but waits for a current service catalogue at startup.
 *
 * Usage:
 *   const { isEnabled } = useFeatureFlags();
 *   if (!isEnabled("tile.vendors")) return null;
 *
 * Missing keys default to TRUE — feature flags are an opt-OUT mechanism so a
 * brand-new tile that hasn't been added to the catalogue yet stays visible.
 */

const STORAGE_KEY = "icaard_feature_flags_v1";

const FeatureFlagContext = createContext({
  flags: {},
  services: [], ready: false, error: false,
  isEnabled: () => true,
  refresh: async () => {},
});

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch (_) {
    return {};
  }
}

export function FeatureFlagProvider({ children }) {
  const [flags, setFlags] = useState(readCache);
  const [services, setServices] = useState([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const latest=useRef(0);

  const refresh = useCallback(async () => {
    const request=++latest.current;
    try {
      const { data } = await api.get("/feature-flags");
      if(request!==latest.current)return;
      const next = (data && data.flags) || {};
      setFlags(next);
      setServices(data.services || []);
      setReady(true);setError(false);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (_) {
        /* quota — ignore */
      }
    } catch (_) {
      if(request===latest.current)setError(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer=setInterval(refresh,30000);
    const visible=()=>{if(document.visibilityState==='visible')refresh();};
    window.addEventListener('focus',refresh);document.addEventListener('visibilitychange',visible);
    const sync=event=>{if(event.key===STORAGE_KEY)refresh();};window.addEventListener('storage',sync);
    return()=>{clearInterval(timer);window.removeEventListener('focus',refresh);document.removeEventListener('visibilitychange',visible);window.removeEventListener('storage',sync);};
  }, [refresh]);

  const isEnabled = useCallback(
    (key) => {
      return flagEnabled(flags,services,key,ready);
    },
    [flags,services,ready]
  );

  return (
    <FeatureFlagContext.Provider value={{ flags, services, ready, error, isEnabled, refresh }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagContext);
}
